import type {DcdOwnershipBreadcrumbType} from 'analytics/dcdOwnershipAnalytics';
import {logDcdOwnershipDetailInteraction} from 'analytics/dcdOwnershipAnalytics';
import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {
  useDcdOwnershipComponent,
  useDcdOwnershipComponentReference,
  useDcdOwnershipComponents,
} from 'components/teams/hooks';
import {
  getComponentsAndServiceReferenceRoute,
  getComponentsAndServicesServiceRoute,
  getDefaultTeamsRoute,
} from 'constant';
import {t} from 'i18next';
import {useMemo} from 'react';
import {useParams} from 'react-router';

import {findDcdOwnershipService, parseIdParam, useDcdOwnershipPageType} from './ownershipHelpers';

export const OwnershipBreadcrumb = () => {
  const {
    componentId: componentIdParam,
    serviceId: serviceIdParam,
    referenceId: referenceIdParam,
  } = useParams<{
    componentId?: string;
    serviceId?: string;
    referenceId?: string;
  }>();
  const {data: components = []} = useDcdOwnershipComponents();

  const selectedComponentId = useMemo(() => parseIdParam(componentIdParam), [componentIdParam]);
  const selectedServiceId = useMemo(() => parseIdParam(serviceIdParam), [serviceIdParam]);
  const selectedReferenceId = useMemo(() => parseIdParam(referenceIdParam), [referenceIdParam]);
  const {data: selectedComponentDetail} = useDcdOwnershipComponent(
    selectedServiceId === undefined ? undefined : selectedComponentId
  );
  const {data: selectedReference} = useDcdOwnershipComponentReference(
    selectedComponentId,
    selectedReferenceId
  );

  const selectedComponent = useMemo(
    () => components.find(({id}) => id === selectedComponentId),
    [components, selectedComponentId]
  );
  const selectedService =
    selectedReference?.service ??
    findDcdOwnershipService(selectedComponentDetail?.services, selectedServiceId);
  const pageType = useDcdOwnershipPageType(selectedReference?.reference_type);
  const trackBreadcrumbClick = (breadcrumbType: DcdOwnershipBreadcrumbType) => () => {
    if (pageType) {
      logDcdOwnershipDetailInteraction('breadcrumb_clicked', {breadcrumbType, pageType});
    }
  };

  return (
    <Breadcrumb
      key={
        selectedReferenceId ?? selectedServiceId ?? selectedComponentId ?? 'components-and-services'
      }
      path={[
        {
          children: t('teams'),
          to: getDefaultTeamsRoute(),
          onClick: trackBreadcrumbClick('teams'),
        },
        {
          children: t('teams_components_and_services'),
          to: getComponentsAndServiceReferenceRoute(),
          onClick: trackBreadcrumbClick('components_and_services'),
        },
        ...(selectedComponent
          ? [
              {
                children: selectedComponent.name,
                to: getComponentsAndServiceReferenceRoute(selectedComponent.id),
                onClick: trackBreadcrumbClick('component'),
              },
            ]
          : []),
        ...(selectedComponent && selectedService
          ? [
              {
                children: selectedService.name,
                to: getComponentsAndServicesServiceRoute(selectedComponent.id, selectedService.id),
                onClick: trackBreadcrumbClick('service'),
              },
            ]
          : []),
        ...(selectedComponent && selectedReference
          ? [
              {
                children: selectedReference.name,
                to: getComponentsAndServiceReferenceRoute(
                  selectedComponent.id,
                  selectedReference.id
                ),
                onClick: pageType ? trackBreadcrumbClick(pageType) : undefined,
              },
            ]
          : []),
      ]}
    />
  );
};
